import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { countryName, useSelectedCountry } from '@/features/country-scope';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge } from '@/shared/components/status-badge';
import { reasonLabel } from '@/shared/lib/catalogue-labels';

import { useProviderAvailability } from '../api/providers.queries';
import type { ProviderAvailabilityParams, ProviderItemState } from '../types/provider.types';

const PAGE_SIZE = 25;

const STATE_LABEL: Record<ProviderItemState, string> = {
  Chosen: 'Chosen',
  Backup: 'Backup',
  Ineligible: 'Cannot supply',
};

const STATE_VARIANT = { Chosen: 'success', Backup: 'info', Ineligible: 'warning' } as const;

/**
 * Where checkout buys from this provider, where it would only fall back to it,
 * and where it cannot be used and why — per country in scope. The backend
 * judges each item with the same rules as checkout; nothing is decided here.
 */
export function AvailabilityPanel({ providerId }: { providerId: number }) {
  const { countries } = useSelectedCountry();
  const [params, setParams] = useState<ProviderAvailabilityParams>({
    page: 1,
    pageSize: PAGE_SIZE,
  });
  const availability = useProviderAvailability(providerId, params);
  const nameOf = (id: number) => {
    const country = countries.find((c) => c.id === id);
    return country ? countryName(country) : `#${id}`;
  };
  const show = (countryId: number, state: ProviderItemState) =>
    setParams({ countryId, state, page: 1, pageSize: PAGE_SIZE });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Where checkout uses this provider</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {availability.isLoading ? (
          <Skeleton className="h-32" />
        ) : availability.error || !availability.data ? (
          <ErrorState error={availability.error} onRetry={() => void availability.refetch()} />
        ) : (
          <>
            <Table aria-label="Provider use by country">
              <TableHeader>
                <TableRow>
                  <TableHead>Country</TableHead>
                  <TableHead className="text-end">Offered items it maps</TableHead>
                  <TableHead className="text-end">Chosen</TableHead>
                  <TableHead className="text-end">Only provider</TableHead>
                  <TableHead className="text-end">Backup</TableHead>
                  <TableHead className="text-end">Cannot supply</TableHead>
                  <TableHead>Main reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {availability.data.countries.map((row) => {
                  const top = Object.entries(row.ineligibleByReason).sort((a, b) => b[1] - a[1])[0];
                  return (
                    <TableRow key={row.countryId}>
                      <TableCell>{nameOf(row.countryId)}</TableCell>
                      <TableCell className="text-end tabular-nums">{row.offered}</TableCell>
                      {(['Chosen', 'soleProvider', 'Backup', 'Ineligible'] as const).map((key) => {
                        const count =
                          key === 'soleProvider'
                            ? row.soleProvider
                            : row[key.toLowerCase() as 'chosen' | 'backup' | 'ineligible'];
                        return (
                          <TableCell key={key} className="text-end tabular-nums">
                            {key === 'soleProvider' || count === 0 ? (
                              count
                            ) : (
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0"
                                aria-label={`Show ${STATE_LABEL[key].toLowerCase()} items in ${nameOf(row.countryId)}`}
                                onClick={() => show(row.countryId, key)}
                              >
                                {count}
                              </Button>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-muted-foreground">
                        {top ? `${reasonLabel(top[0])} (${top[1]})` : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-medium">
                  {params.state
                    ? `${STATE_LABEL[params.state]} in ${nameOf(params.countryId ?? 0)}`
                    : 'All items, every country'}
                </span>
                {params.state && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setParams({ page: 1, pageSize: PAGE_SIZE })}
                  >
                    Show all
                  </Button>
                )}
              </div>
              {availability.data.items.items.length === 0 ? (
                <p className="text-muted-foreground text-xs">
                  {params.state
                    ? 'No items in this state.'
                    : 'This provider maps nothing offered in your countries.'}
                </p>
              ) : (
                <Table aria-label="Items by provider state">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Why / used instead</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availability.data.items.items.map((item) => (
                      <TableRow key={`${item.denominationId}-${item.countryId}`}>
                        <TableCell>
                          <Link to={`/brands/${item.brandId}`} className="hover:underline">
                            {item.brandName} · {item.denominationName}
                          </Link>
                          {!item.sellable && (
                            <span className="text-muted-foreground ms-2 text-xs">
                              (not purchasable)
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{nameOf(item.countryId)}</TableCell>
                        <TableCell>
                          <StatusBadge variant={STATE_VARIANT[item.state]}>
                            {item.isSoleProvider ? 'Only provider' : STATE_LABEL[item.state]}
                          </StatusBadge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {[
                            item.reason ? reasonLabel(item.reason) : null,
                            item.chosenProviderName ? `uses ${item.chosenProviderName}` : null,
                          ]
                            .filter(Boolean)
                            .join(' — ') || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {availability.data.items.totalPages > 1 && (
                <div className="flex items-center justify-end gap-2 text-xs">
                  <span className="text-muted-foreground">
                    Page {availability.data.items.page} of {availability.data.items.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={params.page <= 1}
                    onClick={() => setParams({ ...params, page: params.page - 1 })}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={params.page >= availability.data.items.totalPages}
                    onClick={() => setParams({ ...params, page: params.page + 1 })}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
